import { useEffect, useRef } from "react";

import type { LoadResource } from "@prequel/import-react";
import { useListLoads } from "@prequel/import-react";
import { IdCell } from "@prequel/shared-components";

import ImportJobStatusBadge from "@/components/ImportJobStatusBadge";
import ResourceTable from "@/components/ResourceTable";
import { getJobStatus } from "@/pages/import/jobStatus";
import { useStreamContext } from "@/pages/import/StreamsPage/StreamPage";
import { getDuration, getRelativeSubmittedAt } from "@/types";

const COLUMNS = [
  "Load ID",
  "Status",
  "Submitted",
  "Duration",
  "Processed",
  "Transformed",
  "Valid",
  "Loaded",
  "Errored",
];

const formatCount = (value: number | null | undefined) => value?.toLocaleString() ?? "-";

const useLoadsWithRefresh = (streamId: string, refreshKey: number, params: { pageSize?: number }) => {
  const query = useListLoads(streamId, params);
  const { refetch } = query;
  const previousRefreshKey = useRef(refreshKey);

  useEffect(() => {
    if (previousRefreshKey.current !== refreshKey) {
      previousRefreshKey.current = refreshKey;
      refetch();
    }
  }, [refreshKey, refetch]);

  return query;
};

const LoadsTable = () => {
  const { stream, loadsRefreshKey = 0 } = useStreamContext();
  const streamId = stream.id;

  const useLoads = (params: { pageSize?: number }) => useLoadsWithRefresh(streamId, loadsRefreshKey, params);

  const rowMapper = (load: LoadResource) => ({
    id: load.id,
    to: `/import/streams/${streamId}/loads/${load.id}`,
    cells: [
      { value: <IdCell id={load.id} /> },
      { value: <ImportJobStatusBadge status={getJobStatus(load)} /> },
      { value: getRelativeSubmittedAt(load.submitted_at) },
      { value: getDuration({ started_at: load.started_at, ended_at: load.ended_at }) },
      { value: formatCount(load.records_processed) },
      { value: formatCount(load.records_transformed) },
      { value: formatCount(load.records_valid) },
      { value: formatCount(load.records_loaded) },
      { value: formatCount(load.records_errored) },
    ],
  });

  return (
    <ResourceTable<LoadResource>
      columns={COLUMNS}
      useQuery={useLoads}
      rowMapper={rowMapper}
      emptyMessage="No loads found for this stream."
    />
  );
};

export default LoadsTable;
